import { motion, useInView } from "framer-motion";
import { useEffect, useRef, useState } from "react";

const STATS = [
  { value: 300, prefix: "$", suffix: "M+", label: "Attributable revenue driven", detail: "A decade across fintech, trading and eCommerce." },
  { value: 5, prefix: "$", suffix: "M", label: "Monthly ad spend managed", detail: "Google · Meta · Bing · TikTok · DV360." },
  { value: 450, prefix: "", suffix: "%", label: "Peak ROAS delivered", detail: "Held in the 300–450% band across markets." },
  { value: 40, prefix: "", suffix: "+", label: "Countries scaled", detail: "MENA · Africa · Asia · LATAM · Europe." },
  { value: 300, prefix: "", suffix: "K+", label: "D2C customers acquired", detail: "Via Digital Centaur across categories." },
  { value: 35, prefix: "", suffix: "%", label: "CAC reduction", detail: "Ad disapprovals held below 0.1%." },
];

function CountUp({ value, prefix = "", suffix = "", duration = 1.8 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-120px" });
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!inView) return;
    let rafId;
    const start = performance.now();
    const step = (now) => {
      const elapsed = (now - start) / 1000;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.round(value * eased));
      if (progress < 1) rafId = requestAnimationFrame(step);
    };
    rafId = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafId);
  }, [inView, value, duration]);

  return (
    <span ref={ref} className="font-serif tabular-nums">
      {prefix}
      {display}
      {suffix}
    </span>
  );
}

export default function Achievements() {
  return (
    <section
      id="numbers"
      className="relative container-x py-24 md:py-40"
      data-testid="achievements-section"
    >
      <div className="grid grid-cols-12 gap-6 md:gap-10 mb-16 md:mb-20">
        <div className="col-span-12 md:col-span-4">
          <p className="eyebrow">The Ledger</p>
        </div>
        <div className="col-span-12 md:col-span-8">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="font-serif font-light text-4xl md:text-6xl lg:text-7xl leading-[0.96] tracking-tight text-ink"
            data-testid="achievements-title"
          >
            The numbers.
          </motion.h2>
        </div>
      </div>

      <div className="divide-y divide-stroke border-y border-stroke" data-testid="achievements-list">
        {STATS.map((s, i) => (
          <motion.div
            key={s.label}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-120px" }}
            transition={{ duration: 1, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
            className="grid grid-cols-12 gap-4 md:gap-8 items-baseline py-10 md:py-14"
            data-testid={`achievement-row-${i}`}
          >
            <div className="col-span-2 md:col-span-1 font-mono text-[11px] tracking-[0.28em] text-laser pt-2">
              {String(i + 1).padStart(2, "0")}
            </div>
            <div className="col-span-10 md:col-span-4 text-ink text-5xl md:text-7xl lg:text-8xl leading-none tracking-tighter font-light">
              <CountUp value={s.value} prefix={s.prefix} suffix={s.suffix} />
            </div>
            <div className="col-span-12 md:col-span-7 md:pl-8">
              <div className="font-serif font-light text-ink text-xl md:text-2xl leading-tight">
                {s.label}
              </div>
              <div className="mt-2 font-sans text-sm md:text-base text-steel max-w-xl">
                {s.detail}
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
}
